import { DateTime } from 'luxon';
import { ItemTaxCategory, TaxCode, TaxCategory, TaxCodeRow, TaxJurisdiction, ItemTaxCalculation } from './tax';
import { OrderType } from '../order/order';
import { ItemType } from '../accounting/accounting';
import Dinero from 'dinero.js';

export namespace TaxCodeParsers {
  export class TaxCodeRowJson {
    readonly id!: string;
    readonly name!: string;
    readonly createdAt!: DateTime;
    readonly deletedAt?: DateTime;
  }

  export class TaxJurisdictionRowJson {
    readonly id!: string;
    readonly name!: string;
    readonly percentage!: number;
    readonly createdAt!: DateTime;
    readonly deletedAt?: DateTime;
    readonly source!: string;
    readonly generalLedgerAccountId?: string;
    readonly linkId?: string;
    readonly taxCategories!: TaxCategoryJson[];
  }

  export type TaxCodeJson = {
    readonly totalPercentage: number;
    readonly name: string;
    readonly id: string;
    readonly jurisdictions: TaxJurisdictionRowJson[];
    readonly qboTaxCodeId?: string;
    readonly source: string;
  };

  export type TaxCategoryJson = {
    readonly id: string;
    readonly name: string;
    readonly description?: string;
    readonly qboTaxCodeId?: string;
    readonly createdAt: string;
    readonly deletedAt?: string;
  };

  export type ItemTaxCategoryJson = {
    id: string;
    itemId?: string | number;
    itemType: ItemType;
    transactionType?: OrderType;
    taxCategoryId: string;
    createdAt: DateTime;
    deletedAt?: DateTime;
  };

  export type ItemTaxCalculationJson = {
    taxCodeId?: string;
    taxRate: number;
    tax: Dinero.Options;
    price: Dinero.Options;
    taxCategoryId?: string;
    totalPercentage: number;
    itemId?: string;
    itemType: ItemType;
  };

  export function toItemTaxCalculation(input: ItemTaxCalculationJson): ItemTaxCalculation {
    return {
      taxCodeId: input.taxCodeId,
      taxRate: input.taxRate,
      tax: Dinero(input.tax),
      price: Dinero(input.price),
      taxCategoryId: input.taxCategoryId,
      totalPercentage: input.totalPercentage,
      itemId: input.itemId,
      itemType: input.itemType,
    };
  }

  export function toTaxCodeRow(input: TaxCodeRowJson): TaxCodeRow {
    return {
      id: input.id,
      name: input.name,
      createdAt: input.createdAt,
      deletedAt: input.deletedAt,
    };
  }

  export function toTaxJurisdictionRow(input: TaxJurisdictionRowJson): TaxJurisdiction {
    return {
      id: input.id,
      name: input.name,
      createdAt: input.createdAt,
      deletedAt: input.deletedAt,
      percentage: input.percentage,
      source: input.source as 'manual' | 'TaxJar',
      generalLedgerAccountId: input.generalLedgerAccountId,
      linkId: input.linkId,
      taxCategories: input.taxCategories.map((_) => toTaxCategory(_)),
    };
  }

  export function toTaxCode(input: TaxCodeJson): TaxCode {
    return {
      totalPercentage: input.totalPercentage,
      name: input.name,
      id: input.id,
      jurisdictions: input.jurisdictions.map((_) => toTaxJurisdictionRow(_)),
      qboTaxCodeId: input.qboTaxCodeId,
      source: input.source,
    };
  }

  export function toTaxCategory(input: TaxCategoryJson): TaxCategory {
    return {
      id: input.id,
      name: input.name,
      description: input.description,
      qboTaxCodeId: input.qboTaxCodeId,
      createdAt: DateTime.fromISO(input.createdAt, { setZone: true }),
      deletedAt: input.deletedAt ? DateTime.fromISO(input.deletedAt, { setZone: true }) : undefined,
    };
  }

  export function toItemTaxCategory(input: ItemTaxCategoryJson): ItemTaxCategory {
    return {
      id: input.id,
      itemId: input.itemId,
      itemType: input.itemType,
      transactionType: input.transactionType ?? undefined,
      taxCategoryId: input.taxCategoryId,
      createdAt: input.createdAt,
      deletedAt: input.deletedAt,
    };
  }
}
